import React, { FC, useCallback } from 'react';
import { Dialog } from '@mui/material';
import { Clear } from '@mui/icons-material';
import { FixedSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { ICampaign } from 'reduxState/store/campaigns/types';

interface ICampaignDisableModal {
  isModalOpen: boolean;

  campaign: ICampaign;
  handleCloseModal: () => void;
}

/**
 * CampaignAppIDModal component displays a dialog showing application IDs for a campaign.
 *
 * This modal presents a list of applications associated with the campaign, displaying
 * their IDs and names in a scrollable virtualized list. The list is optimized for
 * performance using react-window virtualization.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isModalOpen - Controls whether the modal is displayed
 * @param {ICampaign} props.campaign - The campaign whose applications will be displayed
 * @param {Function} props.handleCloseModal - Function to close the modal
 *
 * @returns {JSX.Element} A modal dialog displaying a virtualized list of application IDs
 *
 * @example
 * <CampaignAppIDModal
 *   isModalOpen={isModalOpen}
 *   campaign={selectedCampaign}
 *   handleCloseModal={handleCloseModal}
 * />
 */

const CampaignDisableModal: FC<ICampaignDisableModal> = ({ isModalOpen, campaign, handleCloseModal }) => {
  const { applications, title, id } = campaign;

  const appRows = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const application = applications[index];

      return (
        <div
          style={style}
          className={`flex items-center hover:bg-gray-100 transition-colors ${
            index % 2 === 0 ? 'bg-gray-50' : 'bg-white'
          }`}
        >
          <span className="text-xs text-gray-800" data-testid={`app-row-${application.id}`}>
            {application.id} - {application.name}
          </span>
        </div>
      );
    },
    [applications],
  );

  return (
    <Dialog
      open={isModalOpen}
      onClose={handleCloseModal}
      classes={{ paper: 'px-[80px] py-[50px] w-[1400px] min-w-[500px]' }}
      fullWidth={true}
      maxWidth="md"
    >
      <div
        className="absolute top-[15px] right-[15px] cursor-pointer"
        onClick={handleCloseModal}
        data-testid="close-button"
      >
        <Clear />
      </div>
      <div className="flex flex-row justify-between">
        <h3 className="m-0 text-muted-dark-purple">Name: {title}</h3>
        <h3 className="m-0 text-muted-dark-purple">ID: {id}</h3>
      </div>
      <h4 className="text-muted-dark-purple">Applications</h4>
      <div className="border border-gray-200 rounded-md h-[300px]">
        <AutoSizer>
          {({ height, width }: { height: number; width: number }): JSX.Element => (
            <FixedSizeList height={height} width={width} itemSize={32} itemCount={applications.length}>
              {appRows}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>
    </Dialog>
  );
};

export default CampaignDisableModal;
